/**
 * Higher-order component (HOC) for authentication
 * Converted from Next.js to React Router
 *
 * Checks if user is signed in on mount and retrieves user info.
 * Shows loading state while checking authentication.
 * Redirects to login if not authenticated.
 */

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { createSelector } from 'reselect';
import Loading from '../components/Loading.jsx';
import {
  retrieveUser,
  selectCurrentOrganizationId,
  selectIsSignedIn,
} from '../modules/auth.js';
import { retrieveOrganization } from '../modules/organizations.js';
import { navigate } from './router.js';
import { selectSessionExpired } from '../modules/auth.js';

const mapStateToProps = createSelector(
  selectIsSignedIn,
  selectSessionExpired,
  selectCurrentOrganizationId,
  (isSignedIn, sessionExpired, organizationId) => ({
    isSignedIn,
    organizationId,
    sessionExpired,
  })
);
const mapDispatchToProps = { retrieveUser, retrieveOrganization };

const requireAuth = (Component) => {
  const AuthenticatedComponent = ({
    isSignedIn,
    organizationId,
    sessionExpired,
    retrieveUser,
    retrieveOrganization,
    ...props
  }) => {
    const params = useParams();
    const [isChecking, setIsChecking] = useState(true);

    useEffect(() => {
      if (!isSignedIn && !sessionExpired) {
        const slug = params.slug;

        retrieveUser({ organization_slug: slug })
          .then((response) => {
            response.user.organizations.forEach((o) =>
              localStorage.setItem(
                `${o.slug}_CUSTOM_NAMING_SETTING`,
                JSON.stringify(o.customNaming)
              )
            );

            if (!slug && response.user.organizations.length > 0) {
              navigate(`/${response.user.organizations[0].slug}/projects`, {
                replace: true,
              });
            }

            setIsChecking(false);
          })
          .catch(() => {
            setIsChecking(false);
          });
      } else {
        setIsChecking(false);
      }
    }, [isSignedIn, sessionExpired, retrieveUser, params.slug]);

    // Fetch organization data after user is authenticated
    useEffect(() => {
      if (isSignedIn && organizationId) {
        retrieveOrganization(organizationId);
      }
    }, [isSignedIn, organizationId, retrieveOrganization]);

    // Show loading while checking auth
    if (isChecking || (!isSignedIn && !sessionExpired)) {
      return (
        <div className="component-centered">
          <Loading />
        </div>
      );
    }

    // User is authenticated, render the component
    return <Component {...props} />;
  };

  AuthenticatedComponent.displayName = `RequireAuth(${Component.displayName || Component.name || 'Component'})`;

  return connect(mapStateToProps, mapDispatchToProps)(AuthenticatedComponent);
};

export default requireAuth;
